/**
 * ModulesContext — tenant-level module access context.
 *
 * Fetches the current tenant's active subscription and module list once after
 * authentication.  All components can call `useModules()` to check access
 * without making their own API requests.
 *
 * Architecture note: modules are TENANT-based, not user-based.  The owner pays
 * once and every employee in the tenant schema shares the same access.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

interface ModulesContextValue {
  /** Slugs of purchased modules, e.g. ["projects", "rentals"] */
  activeModules: string[];
  /**
   * Returns true if the tenant has access to the module.
   * Also returns true when the slug is not registered as a module at all
   * (mirrors backend RequiresModule behavior: unregistered slug = open access).
   */
  hasModule: (slug: string) => boolean;
  /** True while the initial fetch is in flight. */
  loading: boolean;
}

const ModulesContext = createContext<ModulesContextValue>({
  activeModules: [],
  hasModule: () => false,
  loading: true,
});

export const ModulesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [registeredModules, setRegisteredModules] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // While auth is still initialising, keep our loading=true so ModuleRoute
    // doesn't redirect prematurely on page refresh.
    if (authLoading) return;

    // Auth finished and no user → clear state immediately, no network request needed.
    if (!user) {
      setActiveModules([]);
      setRegisteredModules(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch('/api/subscriptions/api/subscriptions/me/', {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) return { modules: [] as string[], registered_modules: null as string[] | null };
        return res.json() as Promise<{ modules: string[]; registered_modules: string[] | null }>;
      })
      .then((data) => {
        if (!cancelled) {
          setActiveModules(Array.isArray(data.modules) ? data.modules : []);
          setRegisteredModules(Array.isArray(data.registered_modules) ? data.registered_modules : null);
        }
      })
      .catch(() => {
        // Network error or unexpected response — fail open on UI, backend still
        // enforces access via RequiresModule permission class.
        if (!cancelled) setActiveModules([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  const hasModule = useCallback(
    (slug: string): boolean => {
      // If we don't know which modules are registered yet (still loading or error),
      // fall back to checking activeModules only.
      if (registeredModules === null) return activeModules.includes(slug);
      // Unregistered slug = feature not gated by subscriptions → allow access.
      // Mirrors backend: RequiresModule returns True when slug not in Module table.
      if (!registeredModules.includes(slug)) return true;
      // Registered slug = check if tenant has purchased it.
      return activeModules.includes(slug);
    },
    [activeModules, registeredModules],
  );

  return (
    <ModulesContext.Provider value={{ activeModules, hasModule, loading }}>
      {children}
    </ModulesContext.Provider>
  );
};

export const useModules = (): ModulesContextValue => useContext(ModulesContext);

export default ModulesContext;
